/** Must match Procurement.Domain.WellRegions.All on the server (region codes). */
export const WELL_REGION_OPTIONS = [
  { code: 'SOK', label: 'Southern Oklahoma' },
  { code: 'CENTRAL', label: 'Central' },
  { code: 'WTX', label: 'West Texas' },
  { code: 'ROCKY', label: 'Rock Mountain' }
] as const;

export type WellRegionOption = (typeof WELL_REGION_OPTIONS)[number];

export function wellRegionLabel(code: string): string {
  const o = WELL_REGION_OPTIONS.find((x) => x.code === code);
  return o?.label ?? code;
}

/** Prefer catalog name (from GET /api/regions); fall back to static labels then code. */
export function regionDisplayName(
  code: string,
  catalog: readonly { code: string; name: string }[]
): string {
  const row = catalog.find((r) => r.code === code);
  return row?.name ?? wellRegionLabel(code);
}

/** Minimal shape for region catalog lookup (matches <see cref="RegionMasterDto"/>). */
export interface RegionFieldRef {
  id: string;
  name: string;
  county: string;
  state: string;
  closestCity: string;
  milesFromCity?: number;
}

/** Catalog row resolved for a well (region + field id); includes explicit <c>fieldName</c> for display. */
export interface WellRegionFieldInfo {
  id: string;
  fieldName: string;
  county: string;
  state: string;
  closestCity: string;
  milesFromCity?: number;
}

export function toWellRegionFieldInfo(ref: RegionFieldRef | undefined): WellRegionFieldInfo | undefined {
  if (!ref?.id?.trim()) {
    return undefined;
  }
  return {
    id: ref.id,
    fieldName: ref.name?.trim() ?? '',
    county: ref.county ?? '',
    state: ref.state ?? '',
    closestCity: ref.closestCity ?? '',
    milesFromCity: ref.milesFromCity
  };
}

export interface RegionCatalogRow {
  code: string;
  fields?: RegionFieldRef[];
}

/** Resolve a catalog field by region code and field id (for request detail / forms). */
export function findRegionField(
  catalog: readonly RegionCatalogRow[],
  regionCode: string,
  fieldId: string
): RegionFieldRef | undefined {
  const rc = regionCode?.trim();
  const fid = fieldId?.trim();
  if (!rc || !fid) {
    return undefined;
  }
  const region = catalog.find((r) => r.code === rc);
  return region?.fields?.find((f) => f.id === fid);
}
